import * as XLSX from "xlsx";
export const hideBranding = process.env.VUE_APP_HIDE_BRANDING === "true";
import isPostalCode from "validator/lib/isPostalCode";
import validator from "validator";
import moment from "moment";
import { isFRPSubDomain } from "@/router/general";

export function arrayBufferToBase64(buffer) {
  let binary = "";
  let bytes = new Uint8Array(buffer);
  let len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }
  return globalThis.btoa(binary);
}

export const allowedEmails = process.env.VUE_APP_USER_EMAILS.split(",").map((el) => el.trim());

export const insureTrustBrokerId = process.env.VUE_APP_INSURETRUST_BROKER_ID?.split(",").map((el) => el.trim());

export const jlpcBrokerId = process.env.VUE_APP_JLPC_BROKER_ID?.split(",").map((el) => el.trim());

export const groupBy = (arr, property) => {
  if (Array.isArray(arr) && property) {
    return arr.reduce((acc, cur) => {
      acc[cur[property]] = [...(acc[cur[property]] || []), cur];
      return acc;
    }, {});
  }
};

export const deleteKeysFromObject = (obj, keys = []) => {
  if (keys.length === 0) return obj;
  for (const el of keys) delete obj[el];
  return obj;
};

export const openInNewTab = (url) => {
  let fullURL = url;
  if (!url.startsWith("http://") && !url.startsWith("https://")) fullURL = "https://" + url;
  const win = globalThis.open(fullURL, "_blank");
  win.focus();
};

export const removeDuplicates = (array, property) => {
  if (!array) return;

  const uniqueObject = {};

  for (const el of array) {
    let key = el[property];
    uniqueObject[key] = el;
  }

  return Object.values(uniqueObject);
};

export const getInitials = (name, showSingleChar = false) => {
  if (!name) return;
  const nameWords = name?.split(" ");

  if (nameWords?.length === 1 || showSingleChar) {
    // Single word name
    return name?.charAt(0)?.toUpperCase();
  } else {
    // First and last name initials
    const firstNameInitial = nameWords[0].charAt(0).toUpperCase();
    const lastNameInitial = nameWords[nameWords.length - 1].charAt(0).toUpperCase();
    return firstNameInitial + lastNameInitial;
  }
};

export const copyToClipboard = (textToCopy) => {
  return new Promise((resolve, reject) => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        resolve(true);
      })
      .catch((err) => reject(err));
  });
};

export const createXLSXTemplate = (headers, filename = "temp") => {
  if (!Array.isArray(headers) || headers.length === 0) {
    return;
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, { type: "array" });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.xlsx`;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getFirstAndLastName = (fullName) => {
  if (!fullName) return { firstName: "", lastName: "" };
  const nameArray = fullName.trim().split(" ");
  const firstName = nameArray.shift();
  const lastName = nameArray.join(" ");
  return { firstName, lastName };
};

export const downloadLocalFile = (name) => {
  const fileUrl = `/files/${name}`;
  const a = document.createElement("a");
  a.href = fileUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const isValidPostalCode = (postal, country) => {
  const availableLocales = validator.isPostalCodeLocales;
  if (postal && country) return isPostalCode(postal, availableLocales.includes(country) ? country : "any");
  return "";
};

export const calculateTablePages = (items, itemsPerPage) => {
  return Math.ceil(items / itemsPerPage);
};

export const getFormattedDate = (val, format = "MMM Do, YYYY") => {
  return moment(val).format(format);
};

export const addThousandsSeparators = (value) => {
  const digitsOnly = String(value).replaceAll(/\D/g, "");
  if (digitsOnly.length <= 3) return digitsOnly;

  let formatted = "";
  let digitCount = 0;

  for (let index = digitsOnly.length - 1; index >= 0; index -= 1) {
    formatted = digitsOnly[index] + formatted;
    digitCount += 1;

    if (digitCount % 3 === 0 && index !== 0) {
      formatted = `,${formatted}`;
    }
  }

  return formatted;
};

export const formatNumberWithComma = (val) => {
  if (val) {
    return addThousandsSeparators(val);
  }
  return "";
};

export const formatNumberWithDecimal = (val) => {
  if (val) {
    const parts = val.toString().split(".");
    const integerPart = addThousandsSeparators(parts[0]);
    const decimalPart = parts[1] ? "." + parts[1] : "";
    return integerPart + decimalPart;
  }
  return "";
};

/** USD with cents (en-US). Not formatNumberWithComma — that drops decimals. */
export const formatMoneyUsd = (val) => {
  if (val === null || val === undefined || val === "") return "-";
  if (typeof val === "string" && val.trim().startsWith("$")) return val.trim();
  const n = typeof val === "number" ? val : Number(String(val).replaceAll(/[^0-9.-]/g, ""));
  if (Number.isNaN(n)) return String(val).trim() || "-";
  if (n === 0) return "-";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * A stored timestamp as the viewer reads it: "Oct 9, 2026, 12:10 PM" in their locale and
 * time zone. A value with no zone (2026-10-09T09:10:20) is UTC, as the API stores it.
 * Anything that is not a date comes back as the empty string, never as raw text.
 */
export const formatUtcDateTime = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const isDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(raw);
  const date = new Date(isDateTime && !hasZone ? `${raw.replace(" ", "T")}Z` : raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const getLastMonths = (count) => {
  const lastMonths = [];
  for (let i = count - 1; i >= 0; i--) {
    const month = moment().subtract(i, "months").month();
    const monthName = moment.monthsShort(month);
    lastMonths.push(monthName);
  }
  return lastMonths;
};

export const isSystemEmailAddress = (email, checkFRP = true) => {
  if (email && (checkFRP ? isFRPSubDomain() : true)) {
    let emailParts = email.split("secondsight.ai");
    return emailParts.length > 1;
  }
  return false;
};

export function getWebsiteMainUrl(website) {
  try {
    let trimmedWebsite = website.trim();

    // Prepend "https://" if not already present
    if (!/^https?:\/\//i.test(trimmedWebsite)) {
      trimmedWebsite = `https://${trimmedWebsite}`;
    }

    // Create a new URL object
    const url = new URL(trimmedWebsite);

    // Return the formatted hostname
    return url.hostname;
  } catch (error) {
    console.error(error);
    return ""; // Return an empty string for invalid URLs
  }
}

export function objectToQueryString(paramsObject) {
  const sortedParams = Object.keys(paramsObject)
    .sort((a, b) => a.localeCompare(b)) // Ensure keys are sorted alphabetically
    .reduce((acc, key) => {
      acc.append(key, paramsObject[key]); // Append sorted key-value pairs
      return acc;
    }, new URLSearchParams());

  return sortedParams.toString();
}

export function buildIndustryMap(data) {
  const industryMap = {};

  for (const item of data) {
    const orgCode = Number.parseInt(item["NAICS Code (for Organization)"]);
    const sectorCode = Number.parseInt(item["NAICS Code 2-digit"]);
    const subsectorCode = Number.parseInt(item["NAICS Code 4-digit"]);

    industryMap[orgCode] = item["NAICS Description"];

    if (!industryMap[sectorCode]) {
      industryMap[sectorCode] = item["NAICS Description 2-digit"];
    }

    if (!industryMap[subsectorCode]) {
      industryMap[subsectorCode] = item["NAICS Description 4-digit"];
    }
  }

  return industryMap;
}
